import {
  Button,
  Col,
  Dropdown,
  Form,
  Popconfirm,
  Row,
  Space,
  Tooltip,
} from 'antd'
import CIMSSection from '../CIMSSection'
import { CIMSFormItemListProps } from './types'
import { DeleteTwoTone, PlusCircleTwoTone } from '@ant-design/icons'

function CIMSFormItemList<ItemType>({
  children,
  label: labelOrObject,
  help,
  name,
  validate,
  buttons,
  rowOptions,
}: CIMSFormItemListProps<ItemType>) {
  let singular: string, plural: string
  if (typeof labelOrObject === 'string') {
    singular = labelOrObject
    plural = labelOrObject + 's'
  } else {
    singular = labelOrObject.singular
    plural = labelOrObject.plural
  }
  return (
    <CIMSSection title={plural} help={help}>
      <Form.List
        name={name}
        rules={
          validate
            ? [
                {
                  validator: async (_, value: ItemType[]) => {
                    const result = await validate(value)
                    if (result !== true) throw new Error(result)
                  },
                },
              ]
            : undefined
        }
      >
        {(fields, { add, remove }, { errors, warnings }) => (
          <>
            <Space style={{ marginBottom: '1rem' }}>
              {buttons.reduce((l, { loading, disabled, ...button }) => {
                if (
                  button.hideAfter !== undefined &&
                  fields.length > button.hideAfter
                )
                  return l

                const icon =
                  button.icon === 'add' ? <PlusCircleTwoTone /> : button.icon
                const label = button.icon === 'add' ? singular : button.label
                const key = `${button.icon}-${label}-${button.type}`
                if (button.type === 'dropdown') {
                  const { items } = button
                  const disable = loading || disabled || !items?.length
                  l.push(
                    <Dropdown
                      key={key}
                      disabled={disable}
                      menu={{
                        items,
                        style: { maxHeight: 300, overflowY: 'auto' },
                        onClick: ({ key }) => {
                          const value = button.add(key)
                          if (value !== undefined) add(button.add(key))
                        },
                      }}
                    >
                      <Tooltip
                        title={
                          !items?.length
                            ? `There are no ${label} records to select`
                            : undefined
                        }
                      >
                        <Button
                          disabled={disable}
                          loading={loading}
                          icon={icon}
                          type="primary"
                        >
                          {label}
                        </Button>
                      </Tooltip>
                    </Dropdown>
                  )

                  return l
                }
                l.push(
                  <Button
                    key={key}
                    type="primary"
                    loading={loading}
                    disabled={loading || disabled}
                    icon={icon}
                    onClick={() => add(button.add())}
                  >
                    {label}
                  </Button>
                )
                return l
              }, Array<JSX.Element>())}
            </Space>
            {fields.map(({ key, name }) => (
              <Row
                key={key}
                gutter={rowOptions?.gutter ?? 16}
                justify={rowOptions?.justify}
              >
                {children(name)}
                {fields.length > (rowOptions?.showDeleteAfter ?? 0) && (
                  <Col {...rowOptions?.deleteButtonColProps}>
                    <Popconfirm
                      title={`Are you sure you want to remove this ${singular}? This action cannot be undone!`}
                      onConfirm={() => remove(name)}
                    >
                      <Button icon={<DeleteTwoTone />} type="text" />
                    </Popconfirm>
                  </Col>
                )}
              </Row>
            ))}
            <Form.ErrorList errors={errors} warnings={warnings} />
          </>
        )}
      </Form.List>
    </CIMSSection>
  )
}

export default CIMSFormItemList
